import { type RefObject, useEffect, useRef } from "react";
import design from "./bob-design.json";
import { swarmPosition } from "./bob-swarm-motion";
import { isRestingBand, restingBand, thinkingTransition } from "./bob-thinking-motion";

type Dot = { x: number; y: number; seed: number; color: string };

/** Sample each band's real gradient once, including its transparent S-curve seams. */
function loadDots(symbol: HTMLElement) {
  const svg = symbol.querySelector("svg");
  if (!svg) return Promise.reject(new Error("Missing Bob symbol"));
  return Promise.all(
    design.bands.map((_, index) => {
      const clone = svg.cloneNode(true) as SVGSVGElement;
      clone.querySelectorAll<SVGElement>(".bob-wave-band").forEach((band, n) => {
        if (n !== index) band.remove();
        else band.style.removeProperty("opacity");
      });
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.setAttribute("width", "320");
      clone.setAttribute("height", "320");
      return new Promise<Dot[]>((resolve, reject) => {
        const image = new Image();
        image.onerror = reject;
        image.onload = () => {
          try {
            const texture = document.createElement("canvas");
            texture.width = texture.height = 320;
            const paint = texture.getContext("2d", { willReadFrequently: true });
            if (!paint) throw new Error("Canvas unavailable");
            paint.drawImage(image, 0, 0, 320, 320);
            const pixels = paint.getImageData(0, 0, 320, 320).data;
            const dots: Dot[] = [];
            const pitch = 3.05;
            let row = 0;
            for (let y = 2 + pitch / 2; y < 98; y += pitch, row++) {
              for (let x = 2 + pitch / 2 + ((row % 2) * pitch) / 2; x < 98; x += pitch) {
                const pixel = (Math.floor(y * 3.2) * 320 + Math.floor(x * 3.2)) * 4;
                if ((pixels[pixel + 3] ?? 0) < 230) continue;
                dots.push({
                  x,
                  y,
                  seed: ((dots.length + 1) * 0.61803398875 + index * 0.137) % 1,
                  color: `rgb(${pixels[pixel]},${pixels[pixel + 1]},${pixels[pixel + 2]})`,
                });
              }
            }
            resolve(dots);
          } catch (error) {
            reject(error);
          }
        };
        image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
      });
    }),
  );
}

export function BobParticles({
  thinking,
  motionAllowed,
  symbolRef,
}: {
  readonly thinking: boolean;
  readonly motionAllowed: boolean;
  readonly symbolRef: RefObject<HTMLSpanElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const current = useRef(design.bands.map(restingBand));
  const textures = useRef<Promise<Dot[][]> | null>(null);
  const clockOrigin = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const symbol = symbolRef.current;
    const stage = canvas?.closest<HTMLElement>(".bob-swarm-boundary");
    if (!canvas || !symbol || !stage) return;
    const elements = [...symbol.querySelectorAll<SVGElement>(".bob-wave-band")];
    const resetBands = () => {
      current.current = design.bands.map(restingBand);
      elements.forEach((band) => {
        band.style.removeProperty("opacity");
      });
      stage.classList.remove("bob-progressive");
      stage.dataset.phase = "idle";
    };
    if (!motionAllowed || (!thinking && current.current.every(isRestingBand))) {
      resetBands();
      return;
    }
    // Keep the solid, gently pulsing mark while loading or on unsupported canvases.
    stage.classList.add("bob-swarm-fallback");
    let context: CanvasRenderingContext2D | null = null;
    try {
      if (typeof ResizeObserver !== "undefined") context = canvas.getContext("2d");
    } catch {
      // The complete symbol remains usable if a browser disables canvas.
    }
    if (!context) {
      resetBands();
      return;
    }
    const paint = context;
    let cancelled = false;
    let frame = 0;
    let running = false;
    let loading = false;
    let width = 0;
    let height = 0;
    let ratio = 1;
    let startedAt = 0;
    let lastPaint = -Infinity;
    let dots: Dot[][] | null = null;
    let transition = thinkingTransition(thinking, current.current);
    const clear = () => {
      paint.setTransform(1, 0, 0, 1, 0, 0);
      paint.clearRect(0, 0, canvas.width, canvas.height);
    };
    const ready = () => !cancelled && !document.hidden && width >= 64 && height >= 64;

    const draw = (now: number) => {
      frame = 0;
      if (!ready() || !dots) return;
      // Cap both band updates and particle painting at 30fps on high-refresh screens.
      if (now - lastPaint < 1000 / 30 - 0.5) {
        frame = requestAnimationFrame(draw);
        return;
      }
      lastPaint = now;
      const { bands, phase, done } = transition(now - startedAt);
      current.current = bands;
      stage.dataset.phase = phase;
      bands.forEach((band, index) => {
        const element = elements[index];
        if (element) element.style.opacity = String(band.solid);
      });
      clear();
      if (done) {
        running = false;
        resetBands();
        observer.disconnect();
        return;
      }
      const time = (now - (clockOrigin.current ?? now)) / 1000;
      const scale = (Math.min(width, height) * ratio) / 124;
      paint.save();
      paint.setTransform(scale, 0, 0, scale, (width * ratio) / 2, (height * ratio) / 2);
      bands.forEach((state, index) => {
        if (state.alpha < 0.005) return;
        for (const dot of dots?.[index] ?? []) {
          const radius = 1.35 * (1 - state.spread) + (0.56 + 0.24 * dot.seed) * state.spread;
          const position = swarmPosition(
            dot.x - 50,
            dot.y - 50,
            48,
            radius + 0.3,
            state.spread,
            time,
            dot.seed,
            index % 3,
          );
          paint.globalAlpha = state.alpha * (0.8 + 0.2 * dot.seed);
          paint.fillStyle = dot.color;
          paint.beginPath();
          paint.arc(position.x, position.y, radius, 0, Math.PI * 2);
          paint.fill();
        }
      });
      paint.restore();
      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (!ready() || !dots || running) return;
      transition = thinkingTransition(thinking, current.current);
      startedAt = performance.now();
      clockOrigin.current ??= startedAt;
      lastPaint = -Infinity;
      running = true;
      stage.classList.remove("bob-swarm-fallback");
      stage.classList.add("bob-progressive");
      stage.dataset.phase = transition(0).phase;
      frame = requestAnimationFrame(draw);
    };
    const resize = () => {
      if (cancelled) return;
      width = stage.clientWidth;
      height = stage.clientHeight;
      ratio = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.round(width * ratio);
      const nextHeight = Math.round(height * ratio);
      if (canvas.width !== nextWidth) canvas.width = nextWidth;
      if (canvas.height !== nextHeight) canvas.height = nextHeight;
      if (!ready()) {
        cancelAnimationFrame(frame);
        frame = 0;
        running = false;
        clear();
        resetBands();
        stage.classList.add("bob-swarm-fallback");
        return;
      }
      if (dots) start();
      else if (!loading) {
        loading = true;
        // Cache per mounted symbol, including its unique SVG paint identifiers.
        textures.current ??= Promise.resolve().then(() => loadDots(symbol));
        void textures.current
          .then((loaded) => {
            if (cancelled) return;
            dots = loaded;
            start();
          })
          .catch(() => {
            if (cancelled) return;
            textures.current = null;
            resetBands();
          });
      }
    };
    const observer = new ResizeObserver(resize);
    observer.observe(stage);
    resize();
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      clear();
      stage.classList.remove("bob-progressive", "bob-swarm-fallback");
      // Preserve current band frames for the next effect's seamless return or
      // renewed request. Disabled motion resets them before another paint.
    };
  }, [thinking, motionAllowed, symbolRef]);

  return <canvas ref={canvasRef} className="bob-swarm-canvas" />;
}
